import React, { useState, useEffect } from 'react';
import { styled } from 'styled-components';
import Input from '@src/components/styled/Input';
import SmallButton from '@src/components/styled/SmallButton';
import Form from '@src/components/styled/Form';
import { useAccount } from 'wagmi';

const Container = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: flex-start;
  flex-direction: column;

  margin: 10pt;
  padding: 10pt;

  max-width: 30rem;
  min-width: 30rem;
  min-height: 0;

  border: 2pt solid gray;

  font-size: 32pt;

  color: #0000ff;

  z-index: 1000;

  background-color: #c0c0c0;

  box-shadow: 0pt 4pt 4pt 0 rgba(0, 0, 0, 0.5);

  @media only screen and (max-width: 1280px) {
    max-width: 80%;
    min-width: 80%;
    font-size: 1.5rem;
  }

  a {
    padding: 4pt;
    font-size: 3rem;
    cursor: pointer;

    border: 2pt solid gray;

    &:hover {
      color: #ff0000;
    }
  }
`;

const FormContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  margin-top: 10pt;
  border: 2pt solid gray;
  padding-top: 10pt;

  width: 100%;
  min-width: 0;

  color: #0000ff;

  background-color: #c0c0c0;
  text-align: center;

  ${Input} {
    max-width: 14rem;

    @media only screen and (max-width: 720px) {
      font-size: 1rem;   
    }
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const AvatarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 5pt;
  max-height: 200px;
  overflow-y: auto;
  margin: 10pt 0;
  padding: 10pt;
  border: 2pt solid gray;
  background-color: #f0f0f0;

  @media only screen and (max-width: 720px) {
    grid-template-columns: repeat(4, 1fr);
    max-height: 150px;
  }
`;

const AvatarOption = styled.button<{ isSelected: boolean }>`
  border: 2pt solid ${(props) => (props.isSelected ? '#0000ff' : 'gray')};
  background-color: ${(props) => (props.isSelected ? '#e0e0ff' : 'white')};
  padding: 2pt;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #0000ff;
    background-color: #f0f0ff;
  }

  img {
    width: 100%;
    height: auto;
    aspect-ratio: 1/1;
    object-fit: cover;
  }
`;

const AvatarSection = styled.div`
  margin-bottom: 15pt;
`;

const SectionLabel = styled.label`
  display: block;
  margin-bottom: 5pt;
  color: #0000ff;
  font-weight: bold;
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  currentImg: string;
  onSave: (name: string, avatarKey: string) => void;
};

type AvatarMap = Record<string, string>;

export default function EditProfilePanel({
  isOpen,
  onClose,
  currentName,
  currentImg,
  onSave,
}: Props) {
  const [name, setName] = useState(currentName);
  const [selectedAvatarKey, setSelectedAvatarKey] = useState<string>('');
  const [avatars, setAvatars] = useState<AvatarMap>({});
  const [isLoadingAvatars, setIsLoadingAvatars] = useState(false);
  const { address } = useAccount();

  // Find current avatar key from URL
  useEffect(() => {
    if (currentImg && avatars) {
      const foundKey = Object.entries(avatars).find(
        ([key, url]) => url === currentImg,
      )?.[0];
      if (foundKey) {
        setSelectedAvatarKey(foundKey);
      }
    }
  }, [currentImg, avatars]);

  useEffect(() => {
    if (currentName) {
      setName(currentName);
    }
  }, [currentName]);

  // Fetch avatars when panel opens
  useEffect(() => {
    if (isOpen && address && Object.keys(avatars).length === 0) {
      console.log('Fetching avatars for address:', address);
      setIsLoadingAvatars(true);

      // Use the server URL from environment or default to localhost:8080
      const serverUrl =
        process.env.REACT_APP_SERVER_URL || 'http://localhost:8080';
      const apiUrl = `${serverUrl}/api/avatars/${address}`;

      console.log('Making API request to:', apiUrl);

      fetch(apiUrl)
        .then((res) => {
          console.log('Avatar API response status:', res.status);
          console.log('Response headers:', res.headers.get('content-type'));
          return res.json();
        })
        .then((data: AvatarMap) => {
          console.log(
            'Avatar data received:',
            Object.keys(data).length,
            'avatars',
          );
          setAvatars(data);
          setIsLoadingAvatars(false);
        })
        .catch((err) => {
          console.error('Failed to fetch avatars:', err);
          setIsLoadingAvatars(false);
        });
    }
  }, [isOpen, address]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(name, selectedAvatarKey);
    onClose();
  };

  const handleCancel = () => {
    setName(currentName);
    setSelectedAvatarKey('');
    onClose();
  };

  const handleAvatarSelect = (key: string) => {
    setSelectedAvatarKey(key);
  };

  return (
    <>
      <Overlay onClick={handleCancel} />
      <Container>
        <a onClick={handleCancel}>{'X'}</a>
        <FormContainer>
          <Form onSubmit={handleSubmit}>
            <label>
              Name:{' '}
              <Input
                type='text'
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ width: '12rem' }}
                placeholder='Enter your player name'
              />
            </label>

            <AvatarSection>
              <SectionLabel>Choose Avatar:</SectionLabel>
              {isLoadingAvatars ? (
                <div style={{ textAlign: 'center', padding: '20pt' }}>
                  Loading avatars...
                </div>
              ) : Object.keys(avatars).length > 0 ? (
                <AvatarGrid>
                  {Object.entries(avatars).map(([key, url]) => {
                    return (
                      <AvatarOption
                        key={key}
                        type='button'
                        isSelected={selectedAvatarKey === key}
                        onClick={() => handleAvatarSelect(key)}
                      >
                        <img
                          src={url}
                          alt={`Avatar ${key}`}
                          onError={(e) =>
                            console.error(`Failed to load avatar ${key}:`, url)
                          }
                          onLoad={() =>
                            console.log(`Successfully loaded avatar ${key}`)
                          }
                        />
                      </AvatarOption>
                    );
                  })}
                </AvatarGrid>
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '20pt',
                    color: '#ff0000',
                  }}
                >
                  No avatars available. Address: {address || 'No address'},
                  Avatars: {JSON.stringify(avatars)}
                </div>
              )}
            </AvatarSection>
            <SmallButton type='submit' disabled={!selectedAvatarKey}>
              SAVE PROFILE
            </SmallButton>
          </Form>
        </FormContainer>
      </Container>
    </>
  );
}
